/**
 * RunSquare — the 26 px per-run square of §4.5.3, and the only way into a run.
 *
 * Five states, each with a glyph as well as a colour (colour is never the sole
 * carrier) and a spelled-out `aria-label`:
 *   ★ recommandé · ✓ cité jusqu'au bout · digit = décroche à ce tour · – jamais cité
 *   × échec du moteur (excluded from every denominator)
 *
 * Used on the persona matrix (navigate to the run) and repeated in the run view's
 * « Autres runs de cette configuration ».
 */

import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ConversationSubRun } from '@traaker/shared';

export type RunRow = ConversationSubRun['runs'][number];

export type SquareKind = 'fail' | 'reco' | 'kept' | 'never' | 'drop';

export function squareKind(run: RunRow): SquareKind {
  if (run.status === 'failed' || run.outcome === 'x') return 'fail';
  if (run.outcome === 'R') return 'reco';
  if (run.outcome === 'M') return 'kept';
  if (run.outcome === 1) return 'never';
  return 'drop';
}

export const SQUARE_STYLE: Record<SquareKind, { bg: string; fg: string; border: string; text: (r: RunRow) => string }> = {
  fail: { bg: 'transparent', fg: '#9CA3AF', border: '1px dashed #9CA3AF', text: () => '×' },
  reco: { bg: '#10B981', fg: '#FFFFFF', border: '1px solid #059669', text: () => '★' },
  kept: { bg: '#38BDF8', fg: '#0B3A4A', border: '1px solid #0EA5E9', text: () => '✓' },
  never: { bg: '#E5E7EB', fg: '#6B7280', border: '1px solid #D1D5DB', text: () => '–' },
  drop: { bg: '#F59E0B', fg: '#3F2A02', border: '1px solid #D97706', text: (r) => String(r.outcome) },
};

/** Spelled out — never a colour alone (§4.5.3). */
export function squareDesc(run: RunRow, t: (k: string, o?: Record<string, unknown>) => string): string {
  const kind = squareKind(run);
  if (kind === 'fail') return t('conversations.runsOutcomeFailed');
  if (kind === 'reco') return t('conversations.runsOutcomeReco');
  if (kind === 'kept') return t('conversations.runsOutcomeKept');
  if (kind === 'never') return t('conversations.runsOutcomeNever');
  return t('conversations.runsOutcomeDrop', { turn: run.outcome });
}

export function RunSquare({
  run,
  selected = false,
  label,
  onClick,
}: {
  run: RunRow;
  selected?: boolean;
  label: string;
  onClick: () => void;
}) {
  const s = SQUARE_STYLE[squareKind(run)];
  return (
    <Tooltip title={label}>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-current={selected ? 'true' : undefined}
        sx={{
          width: 26, height: 26, p: 0, borderRadius: '6px', cursor: 'pointer',
          bgcolor: s.bg, color: s.fg, border: s.border,
          fontSize: '0.75rem', fontWeight: 700, fontFamily: 'inherit', lineHeight: 1,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          outline: selected ? '2px solid' : 'none',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        }}
      >
        {s.text(run)}
      </Box>
    </Tooltip>
  );
}

/** The 5-state legend that must accompany the grid (§4.5.3) — a colour key is not enough. */
export function RunSquareLegend() {
  const { t } = useTranslation();
  const items: { kind: SquareKind; glyph: string; label: string }[] = [
    { kind: 'reco', glyph: '★', label: t('conversations.runsOutcomeReco') },
    { kind: 'kept', glyph: '✓', label: t('conversations.runsOutcomeKept') },
    { kind: 'drop', glyph: 'n', label: t('conversations.legendDrop') },
    { kind: 'never', glyph: '–', label: t('conversations.runsOutcomeNever') },
    { kind: 'fail', glyph: '×', label: t('conversations.runsOutcomeFailed') },
  ];
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      {items.map((it) => {
        const s = SQUARE_STYLE[it.kind];
        return (
          <Box key={it.kind} sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            <Box
              aria-hidden
              sx={{
                width: 16, height: 16, borderRadius: '4px',
                bgcolor: s.bg, color: s.fg, border: s.border,
                fontSize: '0.5625rem', fontWeight: 700, lineHeight: 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {it.glyph}
            </Box>
            <Typography variant="caption" color="text.secondary">{it.label}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}
